import { useState } from 'react';
import {
  Shield,
  ChevronRight,
  CheckCircle2,
  BookOpen,
  Clock,
  Award,
  AlertCircle,
  Users,
  FileText,
  Lock,
  ArrowLeft,
} from 'lucide-react';
import { TrainingCourse } from '@/models/training.model';

interface LessonContent {
  objectives: string[];
  content: React.ReactNode;
}

interface TrainingModuleViewerProps {
  course: TrainingCourse;
  level: 'executive' | 'management' | 'staff';
  onComplete: (score: number, passed: boolean) => void;
  onBack: () => void;
}

export function TrainingModuleViewer({
  course,
  level,
  onComplete,
  onBack,
}: TrainingModuleViewerProps) {
  const [showQuiz, setShowQuiz] = useState(false);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [submitted, setSubmitted] = useState(false);

  // Get content based on course title and level
  const content = getCourseContent(course.title, level);

  // Calculate score if quiz exists
  const hasQuiz = course.quiz && course.quiz.questions.length > 0;
  const score = hasQuiz
    ? Object.entries(answers).filter(
        ([i, a]) =>
          course.quiz!.questions[parseInt(i)].correctAnswer ===
          course.quiz!.questions[parseInt(i)].options?.[a]
      ).length
    : 0;
  const passingScore = course.passingScore || 70;
  const passed = hasQuiz
    ? (score / course.quiz!.questions.length) * 100 >= passingScore
    : false;

  const handleRetryQuiz = () => {
    setAnswers({});
    setSubmitted(false);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <BookOpen className="w-8 h-8 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{course.title}</h2>
            <div className="flex items-center gap-4 text-gray-500 text-sm mt-1">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" /> {course.duration} minutes
              </span>
              {hasQuiz && (
                <span className="flex items-center gap-1">
                  <Award className="w-4 h-4" /> {course.quiz!.questions.length} questions
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ChevronRight className="w-5 h-5 rotate-180" />
          Back
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">Learning Objectives</h3>
        <ul className="space-y-2">
          {content.objectives.map((obj, i) => (
            <li key={i} className="flex items-start gap-2 text-gray-700">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
              {obj}
            </li>
          ))}
        </ul>
      </div>

      {!showQuiz ? (
        <>
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            {content.content}
          </div>
          <div className="flex justify-end gap-3">
            {hasQuiz ? (
            <button
              onClick={() => setShowQuiz(true)}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Take Quiz <ChevronRight className="w-5 h-5" />
            </button>
            ) : (
              <button
                onClick={() => onComplete(0, true)}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Mark as Complete <CheckCircle2 className="w-5 h-5" />
              </button>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Award className="w-6 h-6 text-blue-600" />
            Knowledge Check
          </h3>
            {!submitted && (
              <button
                onClick={() => setShowQuiz(false)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Content
              </button>
            )}
          </div>
          {hasQuiz && course.quiz && course.quiz.questions.map((q, i) => (
            <div key={q.id} className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <p className="text-gray-900 font-medium mb-4">
                {i + 1}. {q.question}
              </p>
              <div className="space-y-2">
                {q.options?.map((opt, j) => {
                  const isSelected = answers[i] === j;
                  const isCorrect = opt === q.correctAnswer;
                  const showCorrect = submitted && isCorrect;
                  const showIncorrect = submitted && isSelected && !isCorrect;

                  return (
                  <button
                    key={j}
                    onClick={() => !submitted && setAnswers({ ...answers, [i]: j })}
                    disabled={submitted}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      submitted
                          ? showCorrect
                          ? 'bg-green-100 border-2 border-green-500 text-green-800'
                            : showIncorrect
                            ? 'bg-red-100 border-2 border-red-500 text-red-800'
                            : 'bg-gray-50 border-2 border-transparent text-gray-600'
                          : isSelected
                          ? 'bg-blue-100 border-2 border-blue-500 text-blue-800'
                          : 'bg-gray-50 border-2 border-transparent text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                      <div className="flex items-center justify-between">
                        <span>{opt}</span>
                        {submitted && (
                          <>
                            {showCorrect && (
                              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                            )}
                            {showIncorrect && (
                              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                            )}
                          </>
                        )}
                      </div>
                  </button>
                  );
                })}
              </div>
            </div>
          ))}
          {!submitted ? (
            <div className="space-y-3">
            <button
              onClick={() => setSubmitted(true)}
              disabled={Object.keys(answers).length < course.quiz!.questions.length}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Submit Answers ({Object.keys(answers).length}/{course.quiz!.questions.length} answered)
              </button>
              <button
                onClick={() => setShowQuiz(false)}
                className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
            </button>
            </div>
          ) : (
            <div
              className={`p-6 rounded-lg ${
                passed
                  ? 'bg-green-50 border-2 border-green-500'
                  : 'bg-red-50 border-2 border-red-500'
              }`}
            >
              <div className="flex flex-col gap-4">
                <div>
                  <h4
                    className={`text-xl font-bold ${
                      passed ? 'text-green-800' : 'text-red-800'
                    }`}
                  >
                    {passed
                      ? 'Congratulations! You Passed!'
                      : 'Not Quite - Review and Try Again'}
                  </h4>
                  <p className="text-gray-700 mt-1">
                    Score: {score}/{course.quiz!.questions.length} (
                    {Math.round((score / course.quiz!.questions.length) * 100)}%)
                    {!passed && ` - Passing Score: ${passingScore}%`}
                  </p>
                </div>
                <div className="flex gap-3">
                  {passed ? (
                  <button
                    onClick={() => onComplete(score, true)}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    Complete Lesson
                  </button>
                  ) : (
                    <button
                      onClick={handleRetryQuiz}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Retry Quiz
                    </button>
                  )}
                  <button
                    onClick={() => setShowQuiz(false)}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Review Content
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Get course content based on title and level
function getCourseContent(
  title: string,
  level: 'executive' | 'management' | 'staff'
): LessonContent {
  // This will be populated with the actual content from the zip file
  // For now, return placeholder content that matches the structure
  const isHIPAA = title.toLowerCase().includes('hipaa');
  const isCompany = title.toLowerCase().includes('faircare') || title.toLowerCase().includes('company') || title.toLowerCase().includes('welcome');

  if (isHIPAA) {
    return getHIPAAContent(level);
  } else if (isCompany) {
    return getCompanyContent(level);
  }

  return {
    objectives: ['Complete the training module'],
    content: <div>Content not available</div>,
  };
}

function getHIPAAContent(level: 'executive' | 'management' | 'staff'): LessonContent {
  if (level === 'executive') {
    return {
      objectives: [
        'Understand organizational liability under HIPAA',
        'Know executive responsibilities for compliance programs',
        'Recognize financial and reputational risks of violations',
        'Ensure due diligence in privacy and security oversight',
      ],
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              Executive Summary: Why HIPAA Matters to Leadership
            </h3>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <p className="text-gray-700">
                As an executive at FairCare Plus, you bear ultimate responsibility for HIPAA
                compliance. Our business model involves handling Protected Health Information
                (PHI) through our reimbursement platform, making compliance critical to our
                operations and reputation.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-2">Financial Risk</h4>
                  <p className="text-gray-600 text-sm">
                    Penalties up to $1.5M per violation category annually. Average healthcare
                    breach cost: $10.22M (2024).
                  </p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-2">Reputational Risk</h4>
                  <p className="text-gray-600 text-sm">
                    Breaches erode employer and employee trust—the foundation of our transparent
                    healthcare model.
                  </p>
                </div>
              </div>
            </div>
          </section>
          
          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Executive Responsibilities</h3>
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">1. Designate Key Personnel</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>Privacy Officer: Oversees PHI access policies and breach response</li>
                  <li>Security Officer: Manages technical safeguards and system access</li>
                  <li>Both roles must be documented in writing</li>
                </ul>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">2. Establish Written Policies</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>Privacy policies and procedures</li>
                  <li>Security policies for electronic PHI (ePHI)</li>
                  <li>Breach notification procedures</li>
                  <li>Business Associate Agreements (BAAs) for vendors</li>
                  <li>All policies must be reviewed and updated annually</li>
                </ul>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">3. Implement Training Programs</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>Mandatory training for all employees who handle PHI</li>
                  <li>Role-specific training for different departments</li>
                  <li>Annual refresher training required</li>
                  <li>Documentation of all training completion</li>
                </ul>
              </div>
              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">4. Due Diligence Requirements</h4>
                <p className="text-gray-700">
                  Executives can be held personally liable if they fail to exercise reasonable diligence.
                  This means you must actively oversee compliance, not just delegate it. Regular audits,
                  risk assessments, and corrective action plans are essential.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">HIPAA Penalties Overview</h3>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium text-gray-700">Violation Type</span>
                  <span className="font-medium text-gray-700">Maximum Penalty</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Per violation</span>
                  <span className="font-semibold text-red-600">$63,973</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Annual cap per violation category</span>
                  <span className="font-semibold text-red-600">$1,919,173</span>
                </div>
                <div className="flex justify-between items-center border-t pt-2">
                  <span className="text-gray-600">Average data breach cost (2024)</span>
                  <span className="font-semibold text-red-600">$10.22M</span>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">What Constitutes a Breach?</h3>
            <div className="space-y-3">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-gray-700">
                  A breach is any unauthorized use, access, or disclosure of PHI that compromises security or privacy.
                  This includes sending PHI to the wrong recipient, unauthorized system access, or sharing PHI
                  with unauthorized parties.
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Breach Notification Timeline</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li><strong>Within 60 days:</strong> Notify affected individuals</li>
                  <li><strong>Within 60 days:</strong> Notify HHS if breach affects 500+ individuals</li>
                  <li><strong>Immediately:</strong> Notify media if breach affects 500+ in a state/jurisdiction</li>
                  <li><strong>Annually:</strong> Submit annual breach report to HHS</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Key Takeaways</h3>
            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>HIPAA compliance is not optional—it's a legal requirement with serious financial and legal consequences</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Executive oversight is mandatory—delegation alone is insufficient</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Documentation is critical—if it's not written down, it didn't happen</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Proactive prevention is far cheaper than reactive breach response</span>
                </li>
              </ul>
            </div>
          </section>
        </div>
      ),
    };
  }

  if (level === 'management') {
    return {
      objectives: [
        'Identify PHI in daily operations',
        'Apply the Minimum Necessary standard',
        'Supervise team compliance',
        'Recognize and report potential violations',
      ],
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-600" />
              Management Role in HIPAA Compliance
            </h3>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <p className="text-gray-700">
                As a manager, you play a crucial role in ensuring your team follows HIPAA requirements
                in daily operations. You're responsible for both your own compliance and overseeing your
                team's handling of Protected Health Information (PHI).
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">What is Protected Health Information (PHI)?</h3>
            <div className="space-y-4">
              <p className="text-gray-700">
                PHI is any health information that can identify an individual. This includes:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">Identifiers Include:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                    <li>Names (first, last, or initial)</li>
                    <li>Dates (birth, admission, discharge)</li>
                    <li>Phone numbers and addresses</li>
                    <li>Social Security numbers</li>
                    <li>Medical record numbers</li>
                    <li>Account numbers</li>
                    <li>Email addresses</li>
                    <li>Health plan beneficiary numbers</li>
                  </ul>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-2">Health Information Includes:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                    <li>Medical diagnoses and conditions</li>
                    <li>Treatment information</li>
                    <li>Prescription information</li>
                    <li>Payment and billing information</li>
                    <li>Reimbursement receipts with names</li>
                    <li>Claims data</li>
                    <li>Any information about health status</li>
                  </ul>
                </div>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-gray-700 text-sm">
                  <strong>Key Point:</strong> Even if you remove a name, if other information could identify
                  someone (like a combination of date of birth, medical condition, and zip code), it's still PHI.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">The Minimum Necessary Standard</h3>
            <div className="space-y-4">
              <p className="text-gray-700">
                The Minimum Necessary standard requires that you only access, use, or disclose the minimum
                amount of PHI necessary to accomplish a specific task. This is one of the most important
                HIPAA principles for managers to understand and enforce.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Good Practices
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                    <li>Access only PHI needed for your specific task</li>
                    <li>Share only relevant portions, not entire files</li>
                    <li>Verify authorization before accessing records</li>
                    <li>Use de-identified data when possible for analysis</li>
                  </ul>
                </div>
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Violations
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                    <li>Accessing records "just to see"</li>
                    <li>Sharing entire databases when only a subset is needed</li>
                    <li>Providing more information than requested</li>
                    <li>Browsing patient records without a business need</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Supervising Team Compliance</h3>
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">1. Training Oversight</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>Ensure all team members complete required HIPAA training</li>
                  <li>Conduct role-specific training for your department</li>
                  <li>Reinforce key concepts in team meetings</li>
                  <li>Keep training records updated</li>
                </ul>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">2. Access Management</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>Review access logs regularly for unusual activity</li>
                  <li>Ensure team members only have access they need for their role</li>
                  <li>Revoke access immediately when employees leave</li>
                  <li>Report unauthorized access attempts immediately</li>
                </ul>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">3. Incident Response</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>Establish clear reporting procedures for potential violations</li>
                  <li>Investigate incidents promptly and thoroughly</li>
                  <li>Document all incidents and corrective actions</li>
                  <li>Escalate serious incidents to Privacy Officer immediately</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Common Violations to Avoid</h3>
            <div className="space-y-3">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h4 className="font-semibold text-red-900 mb-2">1. Using AI Tools with PHI</h4>
                <p className="text-gray-700 text-sm">
                  <strong>NEVER</strong> paste PHI into ChatGPT, Claude, or any other AI tool. These services
                  are not covered by Business Associate Agreements and using them violates HIPAA. Use
                  de-identified data only, or consult IT for approved tools.
                </p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h4 className="font-semibold text-red-900 mb-2">2. Email Security</h4>
                <p className="text-gray-700 text-sm">
                  Always use encrypted email for PHI. Never send PHI via unencrypted email. Verify recipient
                  email addresses before sending. Use secure file-sharing platforms when sending large files.
                </p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h4 className="font-semibold text-red-900 mb-2">3. Physical Security</h4>
                <p className="text-gray-700 text-sm">
                  Lock screens when away from your desk. Don't leave PHI visible on your screen in public areas.
                  Store physical documents in locked cabinets. Shred documents containing PHI before disposal.
                </p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h4 className="font-semibold text-red-900 mb-2">4. Social Media and Personal Devices</h4>
                <p className="text-gray-700 text-sm">
                  Never post anything about patients, clients, or specific cases on social media—even if
                  anonymized. Don't access PHI on personal devices unless approved and secured. Don't take
                  photos of PHI with personal phones.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">When to Report Potential Violations</h3>
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <p className="text-gray-700 mb-3">
                Report immediately if you suspect or discover:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Unauthorized access to PHI</li>
                <li>PHI sent to the wrong recipient</li>
                <li>Lost or stolen devices containing PHI</li>
                <li>Data breach or security incident</li>
                <li>Team member accessing PHI they shouldn't</li>
                <li>PHI posted or shared inappropriately</li>
              </ul>
              <div className="mt-4 p-3 bg-red-50 rounded border border-red-200">
                <p className="text-red-800 text-sm font-semibold">
                  Report to your Privacy Officer within 24 hours of discovery. Early reporting can prevent
                  a potential breach and minimize damage.
                </p>
              </div>
            </div>
          </section>
        </div>
      ),
    };
  }

  // Staff level
  return {
    objectives: [
      'Recognize what constitutes PHI',
      'Follow security best practices',
      'Understand when and how to report violations',
      'Apply HIPAA principles in daily work',
    ],
    content: (
      <div className="space-y-6">
        <section>
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" />
            HIPAA Essentials for All Employees
          </h3>
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <p className="text-gray-700">
              Every employee at FairCare Plus may come into contact with Protected Health Information (PHI)
              in their work. Understanding HIPAA basics protects our company, our clients, and yourself.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-blue-900 font-semibold">
                Remember: HIPAA violations can result in personal fines up to $63,973 per violation,
                termination, and even criminal charges in severe cases.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-bold text-gray-900 mb-4">What is PHI?</h3>
          <div className="space-y-4">
            <p className="text-gray-700">
              Protected Health Information (PHI) is any information about a person's health that can
              identify them. At FairCare Plus, you might see PHI in:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h4 className="font-semibold text-yellow-900 mb-2">Examples of PHI:</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>Reimbursement receipts with names and medical services</li>
                  <li>Claims forms with diagnoses</li>
                  <li>Email addresses with health information</li>
                  <li>Phone numbers associated with medical claims</li>
                  <li>Account numbers linked to health services</li>
                  <li>Medical invoices or bills</li>
                  <li>Prescription information</li>
                </ul>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-2">NOT PHI:</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>Blank forms or templates</li>
                  <li>General pricing information</li>
                  <li>Company policy documents</li>
                  <li>De-identified data (no names or identifiers)</li>
                  <li>Public health statistics</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-bold text-gray-900 mb-4">Security Best Practices</h3>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Computer Security
              </h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm">
                <li><strong>Lock your screen</strong> every time you step away from your desk (Windows: Windows + L)</li>
                <li>Use strong passwords and never share them</li>
                <li>Don't write passwords on sticky notes</li>
                <li>Only access PHI on company-approved devices</li>
                <li>Keep software updated (automatic updates enabled)</li>
                <li>Don't click suspicious links or download unknown files</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Physical Documents
              </h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm">
                <li>Never leave documents with PHI unattended on your desk</li>
                <li>Store physical documents in locked cabinets</li>
                <li>Use secure shredding bins for disposal</li>
                <li>Don't remove documents containing PHI from the office</li>
                <li>Clear your desk before leaving for the day</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Communication
              </h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm">
                <li>Verify recipient email addresses before sending PHI</li>
                <li>Use encrypted email for PHI (IT can help set this up)</li>
                <li>Never discuss specific cases in public areas or on public transportation</li>
                <li>Don't discuss patient/client information with unauthorized people</li>
                <li>Only access or share PHI when you have a legitimate work need</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-bold text-gray-900 mb-4">What to Do If You See a Violation</h3>
          <div className="space-y-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-semibold text-red-900 mb-2">If you accidentally see PHI you shouldn't:</h4>
              <ol className="list-decimal list-inside space-y-1 text-gray-700 text-sm ml-4">
                <li>Stop viewing it immediately</li>
                <li>Do not share what you saw with anyone</li>
                <li>Report it to your manager or Privacy Officer within 24 hours</li>
                <li>Document when and how it happened</li>
                <li>Follow any corrective actions provided</li>
              </ol>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">If you see someone else violating HIPAA:</h4>
              <ol className="list-decimal list-inside space-y-1 text-gray-700 text-sm ml-4">
                <li>Don't confront them directly unless it's an urgent situation</li>
                <li>Report to your manager or Privacy Officer immediately</li>
                <li>Document what you observed (who, what, when, where)</li>
                <li>Follow up to ensure the issue was addressed</li>
              </ol>
              <p className="text-gray-700 text-sm mt-2 italic">
                Reporting violations protects everyone. HIPAA violations can result in serious consequences
                for both the individual and the company.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-bold text-gray-900 mb-4">Common Mistakes to Avoid</h3>
          <div className="space-y-3">
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-900 mb-2">❌ Using AI Tools with PHI</h4>
              <p className="text-gray-700 text-sm">
                Never paste PHI into ChatGPT, Claude, or any AI chatbot. These tools are not HIPAA-compliant.
                If you need help with PHI, ask your manager or use approved internal tools only.
              </p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-900 mb-2">❌ Accessing Records "Just to Look"</h4>
              <p className="text-gray-700 text-sm">
                Only access PHI when you have a legitimate work need. Browsing records out of curiosity is
                a HIPAA violation, even if you don't share the information.
              </p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-900 mb-2">❌ Sharing Screenshots</h4>
              <p className="text-gray-700 text-sm">
                Don't take screenshots of PHI and share them via text, email, or social media. Use approved
                secure sharing methods only.
              </p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-900 mb-2">❌ Using Personal Devices</h4>
              <p className="text-gray-700 text-sm">
                Don't access or store PHI on your personal phone, tablet, or computer. Only use
                company-approved devices that have proper security measures in place.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-bold text-gray-900 mb-4">Remember</h3>
          <div className="bg-green-50 rounded-lg p-6 border border-green-200">
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>When in doubt, ask your manager or the Privacy Officer</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>It's better to report a potential violation than to ignore it</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Protecting PHI protects our company and our clients</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>HIPAA compliance is everyone's responsibility</span>
              </li>
            </ul>
          </div>
        </section>
      </div>
    ),
  };
}

function getCompanyContent(level: 'executive' | 'management' | 'staff'): LessonContent {
  if (level === 'executive') {
    return {
      objectives: [
        'Articulate the FairCare Plus value proposition',
        'Understand competitive positioning and market opportunity',
        'Communicate compliance and regulatory framework',
        'Explain financial and operational model',
      ],
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              FairCare Plus: Executive Overview
            </h3>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <p className="text-gray-700">
                FairCare Plus is an ERISA-compliant Health Reimbursement Arrangement (HRA) platform
                that enables employers to offer transparent, cash-pay healthcare benefits to their
                employees. Unlike traditional insurance, we empower employees to choose any provider
                while providing reimbursements for eligible healthcare expenses.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Our Value Proposition</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">For Employers</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>30-70% cost savings vs. insurance</li>
                  <li>Predictable healthcare spending</li>
                  <li>No network restrictions</li>
                  <li>Transparent pricing model</li>
                  <li>Compliance and administrative support</li>
                </ul>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-2">For Employees</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>Choose any healthcare provider</li>
                  <li>Cash-pay pricing transparency</li>
                  <li>Fast reimbursement (typically 2-5 days)</li>
                  <li>No deductibles or copays</li>
                  <li>Full control over healthcare decisions</li>
                </ul>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-2">For Providers</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>Guaranteed upfront payment</li>
                  <li>No claims processing</li>
                  <li>No denials or reprocessing</li>
                  <li>Reduced administrative burden</li>
                  <li>Direct patient relationship</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">ERISA Compliance Framework</h3>
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">What is ERISA?</h4>
                <p className="text-gray-700 text-sm">
                  The Employee Retirement Income Security Act (ERISA) governs employee benefit plans,
                  including health reimbursement arrangements. Our HRA structure is fully ERISA-compliant,
                  which means:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm mt-2 ml-4">
                  <li>Employers maintain plan documents and summary plan descriptions</li>
                  <li>Employees have rights to information and appeals</li>
                  <li>Plans must follow fiduciary standards</li>
                  <li>Compliance with federal regulations is mandatory</li>
                </ul>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-2">How We Ensure Compliance</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>Structured HRA plan documentation templates</li>
                  <li>Automated compliance checks and reporting</li>
                  <li>Regular audits and reviews</li>
                  <li>Legal and regulatory updates</li>
                  <li>Fiduciary support and guidance</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Competitive Positioning</h3>
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Feature</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Traditional Insurance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">FairCare Plus</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-900">Provider Network</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Restricted to network</td>
                      <td className="px-4 py-3 text-sm text-green-600 font-semibold">Any provider</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-900">Pricing Transparency</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Hidden/negotiated rates</td>
                      <td className="px-4 py-3 text-sm text-green-600 font-semibold">Cash-pay transparency</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-900">Cost Savings</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Standard rates</td>
                      <td className="px-4 py-3 text-sm text-green-600 font-semibold">30-70% savings</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-900">Reimbursement Speed</td>
                      <td className="px-4 py-3 text-sm text-gray-600">6-8 weeks</td>
                      <td className="px-4 py-3 text-sm text-green-600 font-semibold">2-5 days</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-900">Deductibles/Copays</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Yes</td>
                      <td className="px-4 py-3 text-sm text-green-600 font-semibold">None</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Financial Model</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Revenue Streams</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>Platform fees from employers</li>
                  <li>Administrative services fees</li>
                  <li>Compliance and consulting services</li>
                  <li>Integration and customization fees</li>
                </ul>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-2">Cost Structure</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>Technology platform maintenance</li>
                  <li>Customer support and service</li>
                  <li>Compliance and legal oversight</li>
                  <li>Marketing and sales operations</li>
                  <li>Administrative processing</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Key Differentiators</h3>
            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Technology-first approach:</strong> Automated platform reduces administrative burden</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Transparency focus:</strong> Cash-pay pricing empowers informed healthcare decisions</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Compliance expertise:</strong> Full ERISA and regulatory support built-in</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Provider-friendly model:</strong> Guaranteed payment and reduced administrative overhead</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Flexibility:</strong> Can work alongside traditional insurance or as a standalone solution</span>
                </li>
              </ul>
            </div>
          </section>
        </div>
      ),
    };
  }

  if (level === 'management') {
    return {
      objectives: [
        'Understand the end-to-end reimbursement workflow',
        'Support employers and employees effectively',
        'Handle common questions and concerns',
        'Manage operational processes',
      ],
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-600" />
              FairCare Plus Operations for Managers
            </h3>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <p className="text-gray-700">
                As a manager, you play a critical role in ensuring smooth operations, supporting both
                employers and employees, and maintaining compliance with ERISA and other regulations.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">How Employees Join FairCare Plus</h3>
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">1. Employer Enrollment</h4>
                <p className="text-gray-700 text-sm mb-2">
                  Employers sign up for FairCare Plus and establish an HRA plan for their organization.
                  They determine:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm ml-4">
                  <li>Annual contribution amounts per employee</li>
                  <li>Eligible expense categories</li>
                  <li>Reimbursement limits and schedules</li>
                  <li>Eligibility criteria for employees</li>
                </ul>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">2. Employee Onboarding</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm ml-4">
                  <li>Employees are enrolled by their employer</li>
                  <li>They receive account access and welcome materials</li>
                  <li>Training on how to submit reimbursement requests</li>
                  <li>Information about eligible expenses and processes</li>
                </ul>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">3. Account Access</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm ml-4">
                  <li>Employees receive login credentials</li>
                  <li>Access to reimbursement portal</li>
                  <li>View account balance and transaction history</li>
                  <li>Submit reimbursement requests</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">End-to-End Reimbursement Workflow</h3>
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Step 1: Employee Receives Healthcare Service</h4>
                <p className="text-gray-700 text-sm">
                  Employee visits any healthcare provider of their choice and pays cash-pay rates
                  directly to the provider.
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Step 2: Submit Reimbursement Request</h4>
                <p className="text-gray-700 text-sm mb-2">
                  Employee submits reimbursement request through our platform with:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm ml-4">
                  <li>Receipt or invoice from provider</li>
                  <li>Date of service</li>
                  <li>Provider name and information</li>
                  <li>Amount paid</li>
                  <li>Type of service (must be eligible expense)</li>
                </ul>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Step 3: Review and Processing</h4>
                <p className="text-gray-700 text-sm mb-2">
                  Our system reviews the request for:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm ml-4">
                  <li>Eligible expense category</li>
                  <li>Available account balance</li>
                  <li>Completeness of documentation</li>
                  <li>Compliance with plan rules</li>
                </ul>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-2">Step 4: Reimbursement</h4>
                <p className="text-gray-700 text-sm">
                  Once approved, reimbursement is processed and funds are typically available to the
                  employee within 2-5 business days via direct deposit or check.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Common Questions from Employers</h3>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Q: Is FairCare Plus insurance?</h4>
                <p className="text-gray-700 text-sm">
                  <strong>A:</strong> No, FairCare Plus is not insurance. It's an ERISA-compliant Health
                  Reimbursement Arrangement (HRA) that reimburses employees for eligible healthcare expenses.
                  Employees can choose any provider and pay cash-pay rates, then receive reimbursement.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Q: Can we use this alongside traditional insurance?</h4>
                <p className="text-gray-700 text-sm">
                  <strong>A:</strong> Yes, many employers use FairCare Plus alongside traditional insurance.
                  For example, they might offer FairCare Plus for routine care and traditional insurance for
                  major medical events. We can help design a complementary benefit structure.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Q: What are the compliance requirements?</h4>
                <p className="text-gray-700 text-sm">
                  <strong>A:</strong> We handle most compliance requirements, including ERISA plan documentation,
                  summary plan descriptions, and regulatory updates. Employers need to maintain plan documents
                  and follow fiduciary responsibilities, which we support through our platform and consulting.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Q: How do we handle employee questions?</h4>
                <p className="text-gray-700 text-sm">
                  <strong>A:</strong> We provide comprehensive support including training materials, help desk
                  support, and dedicated account management. Employees can submit questions through our platform,
                  and we handle most inquiries directly.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Common Questions from Employees</h3>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Q: How do I get reimbursed?</h4>
                <p className="text-gray-700 text-sm">
                  <strong>A:</strong> Simply upload your receipt or invoice through our platform after receiving
                  healthcare services. We'll review and process your request, typically within 2-5 business days.
                  Funds are deposited directly to your bank account or sent by check.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Q: What expenses are eligible?</h4>
                <p className="text-gray-700 text-sm">
                  <strong>A:</strong> Eligible expenses typically include medical care, prescriptions, dental
                  and vision services, preventive care, and other IRS-qualified medical expenses. Your employer's
                  specific plan document will detail what's covered for your organization.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Q: Can I use any doctor?</h4>
                <p className="text-gray-700 text-sm">
                  <strong>A:</strong> Yes! One of the major benefits of FairCare Plus is that you can choose
                  any healthcare provider. There are no network restrictions. You can see any doctor, specialist,
                  or healthcare facility you prefer.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Q: What if my reimbursement is denied?</h4>
                <p className="text-gray-700 text-sm">
                  <strong>A:</strong> If a reimbursement is denied, you'll receive an explanation. Common reasons
                  include expenses not eligible under your plan, insufficient documentation, or exceeding account
                  balance. You can appeal denials through our platform, and we'll review with you and your employer.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Provider Benefits and Communication</h3>
            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <h4 className="font-semibold text-green-900 mb-3">Key Points to Communicate to Providers:</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>FairCare Plus patients pay cash-pay rates upfront (often 30-70% lower than insurance rates)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>No claims processing—patient pays directly, then gets reimbursed by their employer</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>No denials, no pre-authorizations, no network restrictions</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Patients have full account balance available upfront—guaranteed payment</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Reduced administrative burden compared to insurance billing</span>
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Operational Best Practices</h3>
            <div className="space-y-3">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">1. Documentation Standards</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm ml-4">
                  <li>Maintain clear records of all reimbursement requests</li>
                  <li>Document reasons for denials or appeals</li>
                  <li>Keep plan documents current and accessible</li>
                  <li>Regularly audit for compliance</li>
                </ul>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">2. Communication Protocols</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm ml-4">
                  <li>Respond to inquiries within 24 hours</li>
                  <li>Provide clear, consistent information</li>
                  <li>Escalate complex issues to appropriate teams</li>
                  <li>Document all customer interactions</li>
                </ul>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">3. Quality Assurance</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm ml-4">
                  <li>Regular review of reimbursement processing times</li>
                  <li>Monitor error rates and common issues</li>
                  <li>Conduct periodic audits of plan compliance</li>
                  <li>Gather feedback from employers and employees</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      ),
    };
  }

  // Staff level
  return {
    objectives: [
      'Understand what FairCare Plus does',
      'Know our mission and values',
      'Recognize how your role contributes',
      'Support customers effectively',
    ],
    content: (
      <div className="space-y-6">
        <section>
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-600" />
            Welcome to FairCare Plus
          </h3>
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <p className="text-gray-700">
              Welcome to FairCare Plus! We're a healthcare benefits platform that helps employers
              provide transparent, affordable healthcare options to their employees. Our mission is
              to make healthcare more accessible, transparent, and affordable for everyone.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Our Mission</h4>
              <p className="text-gray-700 text-sm">
                To empower employees with choice and transparency in healthcare while providing employers
                with a cost-effective, compliant benefits solution that puts people first.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-bold text-gray-900 mb-4">What Does FairCare Plus Do?</h3>
          <div className="space-y-4">
            <p className="text-gray-700">
              FairCare Plus is not insurance. Instead, we provide an ERISA-compliant Health Reimbursement
              Arrangement (HRA) platform that allows employers to reimburse employees for healthcare expenses.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-2">How It Works</h4>
                <ol className="list-decimal list-inside space-y-1 text-gray-700 text-sm">
                  <li>Employer sets up an HRA plan for employees</li>
                  <li>Employee visits any healthcare provider</li>
                  <li>Employee pays cash-pay rates (often 30-70% less than insurance)</li>
                  <li>Employee uploads receipt through our platform</li>
                  <li>We process reimbursement (typically 2-5 days)</li>
                  <li>Employee receives reimbursement directly</li>
                </ol>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Key Benefits</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>No network restrictions—choose any provider</li>
                  <li>Transparent cash-pay pricing</li>
                  <li>Fast reimbursement (2-5 business days)</li>
                  <li>No deductibles or copays</li>
                  <li>30-70% cost savings for employers</li>
                  <li>Full control for employees</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-bold text-gray-900 mb-4">Our Values</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-900 mb-2">Transparency</h4>
              <p className="text-gray-700 text-sm">
                We believe in clear, honest communication about healthcare costs and benefits.
                No hidden fees, no confusing networks—just straightforward information.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-2">Empowerment</h4>
              <p className="text-gray-700 text-sm">
                We empower employees to make informed healthcare decisions by giving them
                choice, control, and transparent pricing information.
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Innovation</h4>
              <p className="text-gray-700 text-sm">
                We use technology to simplify healthcare benefits, reduce administrative burden,
                and make the reimbursement process fast and easy.
              </p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h4 className="font-semibold text-amber-900 mb-2">Integrity</h4>
              <p className="text-gray-700 text-sm">
                We operate with the highest ethical standards, maintaining full compliance with
                ERISA and other regulations while putting our customers first.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-bold text-gray-900 mb-4">How Your Role Contributes</h3>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Customer Support Roles</h4>
              <p className="text-gray-700 text-sm mb-2">
                If you interact with customers, you're helping to:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm ml-4">
                <li>Answer questions and resolve issues quickly</li>
                <li>Guide employees through the reimbursement process</li>
                <li>Help employers understand their benefits options</li>
                <li>Ensure customers have a positive experience</li>
              </ul>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-2">Operations Roles</h4>
              <p className="text-gray-700 text-sm mb-2">
                If you work in operations, you're helping to:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm ml-4">
                <li>Process reimbursements accurately and efficiently</li>
                <li>Maintain system reliability and performance</li>
                <li>Ensure compliance with regulations</li>
                <li>Support smooth day-to-day operations</li>
              </ul>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-900 mb-2">All Roles</h4>
              <p className="text-gray-700 text-sm mb-2">
                Every employee at FairCare Plus contributes to our mission by:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm ml-4">
                <li>Representing our values in every interaction</li>
                <li>Helping to improve processes and customer experience</li>
                <li>Maintaining compliance and quality standards</li>
                <li>Supporting each other as a team</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-bold text-gray-900 mb-4">Common Questions You Might Hear</h3>
          <div className="space-y-3">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Q: Is this insurance?</h4>
              <p className="text-gray-700 text-sm">
                <strong>A:</strong> No, FairCare Plus is not insurance. It's an HRA that reimburses
                employees for eligible healthcare expenses. Employees can choose any provider and pay
                cash-pay rates, then get reimbursed.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Q: How long does reimbursement take?</h4>
              <p className="text-gray-700 text-sm">
                <strong>A:</strong> Typically 2-5 business days once the request is approved. This is
                much faster than traditional insurance claims processing, which can take 6-8 weeks.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Q: Can employees use any doctor?</h4>
              <p className="text-gray-700 text-sm">
                <strong>A:</strong> Yes! There are no network restrictions. Employees can see any
                healthcare provider they choose—no referrals needed, no network limitations.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Q: How much can employees save?</h4>
              <p className="text-gray-700 text-sm">
                <strong>A:</strong> Employees typically see 30-70% savings compared to insurance rates
                because they pay cash-pay prices, which are often significantly lower than negotiated
                insurance rates. Employers also save substantially on healthcare costs.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-bold text-gray-900 mb-4">Remember</h3>
          <div className="bg-green-50 rounded-lg p-6 border border-green-200">
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Every interaction is an opportunity to represent FairCare Plus values</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Your work directly helps people access affordable, transparent healthcare</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>When in doubt, ask your manager or refer to our documentation</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>We're all part of a team working toward the same mission</span>
              </li>
            </ul>
          </div>
        </section>
      </div>
    ),
  };
}
